"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { DEFAULT_LOCALE, LOCALE_COOKIE, LOCALE_STORAGE_KEY, setCurrentLocale, type Locale } from "./core";

export * from "./core";

type I18nContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
};

const I18nContext = createContext<I18nContextValue>({
  locale: DEFAULT_LOCALE,
  setLocale: () => undefined,
});

export function LanguageProvider({
  initialLocale = DEFAULT_LOCALE,
  children,
}: {
  initialLocale?: Locale;
  children: ReactNode;
}) {
  // Set before children render so the first render already uses the right locale.
  setCurrentLocale(initialLocale);
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  const setLocale = useCallback((next: Locale) => {
    setCurrentLocale(next);
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, next);
      document.cookie = `${LOCALE_COOKIE}=${next}; path=/; max-age=31536000; samesite=lax`;
      document.documentElement.lang = next;
    } catch {
      // Storage can be unavailable (private mode); the in-memory locale still applies.
    }
    setLocaleState(next);
  }, []);

  // The module-level locale is shared per server process, so the browser (a single
  // user) re-asserts its own locale once mounted.
  useEffect(() => {
    setCurrentLocale(locale);
  }, [locale]);

  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * Remounts its children when the locale changes so every non-hook t() call in
 * the subtree re-evaluates. Wrap the UI that shows text, not stateful shells.
 */
export function LocaleBoundary({ children }: { children: ReactNode }) {
  const { locale } = useI18n();
  return (
    <div key={locale} className="contents">
      {children}
    </div>
  );
}

export function useI18n(): I18nContextValue {
  return useContext(I18nContext);
}
